import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import config from './config';
import SidebarPageLayout from './SidebarPageLayout';
import useActiveUserId, { prepareSessionResume } from './useActiveUserId';

const formatShortDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';

// The suggestion currently being tried: last attempt without an outcome,
// falling back to the originally chosen action.
const getCurrentSuggestion = (problem = {}) => {
  const attempts = Array.isArray(problem.attempts) ? problem.attempts : [];
  const active = [...attempts].reverse().find((attempt) => attempt && !attempt.outcome);
  return active?.suggestion || problem.action || {};
};

const PROBLEM_STATUS_CHIPS = {
  open: { label: 'Trying now', className: 'border-[#b7ddf1] bg-[#d9eef9] text-[#0f4e71]' },
  improving: { label: 'Improving', className: 'border-[#b8e0c3] bg-[#e5f6ea] text-[#1f7a3b]' },
  referred: { label: 'Discuss with your doctor', className: 'border-[#f2c180] bg-[#fff0dc] text-[#9a5800]' },
};

// Referred problems carry a to-do (contact the doctor), so they sort first.
const PROBLEM_STATUS_ORDER = { referred: 0, open: 1, improving: 2 };

// "My Two-Week Focus": the suggestions the caregiver chose to try and how
// each one is going. Moved out of Home so the home page stays simple.
function FocusPage() {
  const navigate = useNavigate();
  const activeUserId = useActiveUserId();
  const [problems, setProblems] = useState([]);

  const fetchProblems = useCallback(async (userId) => {
    if (!userId) {
      setProblems([]);
      return;
    }

    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/problems`, {
        params: { userId },
      });
      setProblems(res.data?.problems || []);
    } catch (err) {
      console.error('Error fetching problems:', err);
    }
  }, []);

  useEffect(() => {
    fetchProblems(activeUserId);
  }, [activeUserId, fetchProblems]);

  const focusProblems = problems
    .filter((problem) => PROBLEM_STATUS_ORDER[problem.status] != null)
    .sort((a, b) => PROBLEM_STATUS_ORDER[a.status] - PROBLEM_STATUS_ORDER[b.status]);

  return (
    <SidebarPageLayout>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="vica-title !text-[2.2rem]">My Two-Week Focus</h1>
          <p className="vica-subtitle !text-[1.3rem] leading-8">
            Actions you committed to in your care conversations.
          </p>
        </div>
        <button
          className="vica-btn-secondary vica-session-btn-new shrink-0 justify-center"
          onClick={() => navigate('/home')}
        >
          <ArrowBackIcon sx={{ fontSize: 28 }} />
          Back
        </button>
      </div>

      {focusProblems.length === 0 ? (
        <div className="vica-empty-state !text-[1.25rem] leading-8">
          No active focus yet. When you choose a suggestion during a conversation, it will appear here.
        </div>
      ) : (
        <div className="space-y-3">
          {focusProblems.map((problem) => (
            <div
              key={problem.id}
              className="flex flex-col gap-4 rounded-2xl border border-[#cfe2ee] bg-[#f7fbfe] px-6 py-5 md:flex-row md:items-center"
            >
              <div className="flex-1 min-w-0">
                {PROBLEM_STATUS_CHIPS[problem.status] && (
                  <div className="mb-2">
                    <span className={`vica-tag border !text-[1.05rem] !px-3.5 !py-1.5 ${PROBLEM_STATUS_CHIPS[problem.status].className}`}>
                      {PROBLEM_STATUS_CHIPS[problem.status].label}
                    </span>
                  </div>
                )}
                <div className="text-[1.55rem] leading-9 font-extrabold text-[#0f3950]">
                  {getCurrentSuggestion(problem).title || 'Chosen action'}
                </div>
                <div className="mt-1 text-[1.3rem] leading-8 text-[#1c3f52]">{getCurrentSuggestion(problem).advice}</div>
                <div className="mt-2 text-[1.1rem] font-semibold text-[#6b8798]">
                  Started {formatShortDate(problem.createdAt)} · Check-in by {formatShortDate(problem.dueAt)}
                </div>
              </div>
              {problem.sessionId && (
                <div className="shrink-0">
                  <button
                    className="vica-btn-secondary vica-session-btn-new justify-center !w-[9.5rem] !whitespace-normal !text-center !leading-7"
                    onClick={() => {
                      prepareSessionResume(problem.sessionId);
                      navigate('/chat');
                    }}
                  >
                    Review conversation
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </SidebarPageLayout>
  );
}

export default FocusPage;
